/* STUB IMPLEMENTATION — see src/stubs/README.md.
 *
 * Everything on screen that exists only because there is no server. Kept in
 * this directory so that removing the stubs removes the notices with them,
 * rather than leaving a "demonstration data" banner over live data.
 *
 * Note what these controls are and are not. The entry switch, the presence
 * switch and the provider-availability switch stand in for things a deployment
 * decides elsewhere — two hosts, an identity provider, a deployment setting.
 * None of them is a feature of the console, and each disappears with this
 * directory.
 */

import { useI18n } from '../i18n/useI18n';
import { entryPoint } from '../entry';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/primitives';
import { STUB_ACCOUNTS, STUB_PASSWORD } from './fixtures';
import { STUB_TENANCY_DOMAIN, switchStubEntry } from './stubEntry';
import {
  readAuditExport,
  readClusterReachable,
  readIdentity,
  readNameable,
  readStubRepaired,
  readStubSoaked,
  readVouch,
  setAuditExport,
  setClusterReachable,
  setNameable,
  setProviderAvailable,
  setStubRepaired,
  setStubSoaked,
  setVouch,
} from './stubState';
import type { StubVouch } from './stubState';

/** Shown on every screen inside the shell, both consoles. */
export function StubBanner() {
  const { t } = useI18n();

  return (
    <Alert tone="warn" title={t('stub.banner.title')}>
      <p>{t('stub.banner.body')}</p>
      <StubEntrySwitch />
    </Alert>
  );
}

/**
 * In a deployment these are two hosts and there is no switch. Here they are
 * one, so the two entry points need a door between them — and it reloads,
 * because crossing between them for real is crossing origins.
 */
export function StubEntrySwitch() {
  const { t } = useI18n();
  const entry = entryPoint();

  return (
    <div style={{ marginTop: 10 }}>
      <p>
        {entry.kind === 'account'
          ? t('stub.entry.account')
          : t('stub.entry.cluster', { domain: STUB_TENANCY_DOMAIN })}
      </p>
      <div className="row" style={{ marginTop: 8 }}>
        <Button
          size="sm"
          onClick={() => {
            switchStubEntry(entry.kind === 'account' ? 'cluster' : 'account');
          }}
        >
          {entry.kind === 'account' ? t('stub.entry.toCluster') : t('stub.entry.toAccount')}
        </Button>
      </div>
    </div>
  );
}

/** The accounts the account console signs in, on the account sign-in screen. */
export function StubAccounts() {
  const { t } = useI18n();

  return (
    <Alert tone="warn" title={t('stub.accounts.title')}>
      <p>{t('stub.accounts.admin', { email: STUB_ACCOUNTS.admin })}</p>
      <p>{t('stub.accounts.support', { email: STUB_ACCOUNTS.support })}</p>
      <p>{t('stub.accounts.tenancy', { email: STUB_ACCOUNTS.tenancy })}</p>
      <p>{t('stub.accounts.password', { password: STUB_PASSWORD })}</p>
      <p style={{ marginTop: 8 }}>{t('stub.accounts.attempts')}</p>
      <StubEntrySwitch />
    </Alert>
  );
}

/** How to see a change refused, and how to see the option withdrawn. */
export function StubIdentityProviderHint({ onChanged }: { onChanged: () => void }) {
  const { t } = useI18n();
  const available = readIdentity().provider_available;

  return (
    <Alert tone="warn" title={t('stub.identity.title')}>
      <p>{t('stub.identity.timing')}</p>
      <p style={{ marginTop: 8 }}>{t('stub.identity.rejected')}</p>
      <p style={{ marginTop: 8 }}>{t('stub.identity.failed')}</p>
      <p style={{ marginTop: 8 }}>{t('stub.identity.provider')}</p>
      <div className="row" style={{ marginTop: 8 }}>
        <Button
          size="sm"
          onClick={() => {
            setProviderAvailable(!available);
            onChanged();
          }}
        >
          {available ? t('stub.identity.provider.off') : t('stub.identity.provider.on')}
        </Button>
      </div>
    </Alert>
  );
}

const VOUCHES: StubVouch[] = ['admin', 'stranger', 'refused', 'unreachable'];

/**
 * What the pretend identity provider will answer to the next sign-in.
 *
 * The round trip itself is real — the browser leaves for an address, comes back
 * with a code and a state, and the console exchanges them — so what is being
 * chosen here is only what the far end says. That is the part a demonstration
 * has no other way to produce, and the part worth seeing all four of.
 */
export function StubClusterProvider() {
  const { t } = useI18n();
  const current = readVouch();
  const label: Record<StubVouch, string> = {
    admin: t('stub.cluster.as.admin'),
    stranger: t('stub.cluster.as.stranger'),
    refused: t('stub.cluster.as.refused'),
    unreachable: t('stub.cluster.as.unreachable'),
  };

  return (
    <Alert tone="warn" title={t('stub.cluster.title')}>
      <p>{t('stub.cluster.body')}</p>
      <p style={{ marginTop: 8 }}>{t('stub.cluster.expired')}</p>
      <p style={{ marginTop: 8 }}>{t('stub.cluster.vouches')}</p>
      <div className="row" style={{ marginTop: 8 }}>
        {VOUCHES.map((vouch) => (
          <Button
            key={vouch}
            size="sm"
            variant={vouch === current ? 'primary' : 'ghost'}
            onClick={() => {
              setVouch(vouch);
            }}
          >
            {label[vouch]}
          </Button>
        ))}
      </div>
    </Alert>
  );
}

/**
 * The difference the consumption screen exists to draw: a figure that is zero
 * and a figure that is not there.
 *
 * The default is the deployment as it stands — the tenancy's exported audit
 * chain is out of the control plane's reach, so sessions and people arrive
 * missing with a reason. Switching it on counts them, and among the counted
 * figures is a genuine zero, which has to look nothing like the missing one.
 */
export function StubUsageHint({ onChanged }: { onChanged: () => void }) {
  const { t } = useI18n();
  const readable = readAuditExport();

  return (
    <Alert tone="warn" title={t('stub.usage.title')}>
      <p>{t('stub.usage.body')}</p>
      <p style={{ marginTop: 8 }}>{t('stub.usage.other')}</p>
      <div className="row" style={{ marginTop: 8 }}>
        <Button
          size="sm"
          onClick={() => {
            setAuditExport(!readable);
            onChanged();
          }}
        >
          {readable ? t('stub.usage.export.off') : t('stub.usage.export.on')}
        </Button>
      </div>
    </Alert>
  );
}

/**
 * The version board, and the two things about it a demonstration cannot wait
 * for.
 *
 * Most of it happens by itself: a wave moves, one tenancy in it fails to move on
 * purpose, and the refusal to sign that wave off names it. Two things do not.
 * The soak is half an hour of standing still, which is exactly the sort of thing
 * nobody watches. And putting the failed tenancy right happens at its cluster,
 * not in this console — but without it the way out of a wave a tenancy failed in
 * could never be shown, so it is a switch too.
 */
export function StubRolloutHint({ onChanged }: { onChanged: () => void }) {
  const { t } = useI18n();
  const soaked = readStubSoaked();
  const repaired = readStubRepaired();

  return (
    <Alert tone="warn" title={t('stub.rollout.title')}>
      <p>{t('stub.rollout.body')}</p>
      <p style={{ marginTop: 8 }}>{t('stub.rollout.waves')}</p>
      <p style={{ marginTop: 8 }}>{t('stub.rollout.soak')}</p>
      <p style={{ marginTop: 8 }}>{t('stub.rollout.repaired')}</p>
      <div className="row" style={{ marginTop: 8 }}>
        <Button
          size="sm"
          onClick={() => {
            setStubSoaked(!soaked);
            onChanged();
          }}
        >
          {soaked ? t('stub.rollout.soak.off') : t('stub.rollout.soak.on')}
        </Button>
        <Button
          size="sm"
          onClick={() => {
            setStubRepaired(!repaired);
            onChanged();
          }}
        >
          {repaired ? t('stub.rollout.repaired.off') : t('stub.rollout.repaired.on')}
        </Button>
      </div>
    </Alert>
  );
}

/**
 * The two halves of a write, and the two ways each of them does not go through.
 *
 * A cluster that cannot be reached leaves the declaration standing — the row
 * appears in the list and the screen says the cluster has not been told. A name
 * the cluster will not record stops the write before either half, and nothing
 * changes anywhere. They read very differently on screen, which is the point.
 */
export function StubCatalogueHint({ onChanged }: { onChanged: () => void }) {
  const { t } = useI18n();
  const reachable = readClusterReachable();
  const nameable = readNameable();

  return (
    <Alert tone="warn" title={t('stub.catalog.title')}>
      <p>{t('stub.catalog.body')}</p>
      <div className="row" style={{ marginTop: 8 }}>
        <Button
          size="sm"
          onClick={() => {
            setClusterReachable(!reachable);
            onChanged();
          }}
        >
          {reachable ? t('stub.catalog.reachable.off') : t('stub.catalog.reachable.on')}
        </Button>
        <Button
          size="sm"
          onClick={() => {
            setNameable(!nameable);
            // The name is part of the session the console is holding, so this
            // is a page load in the demonstration as it would be in a
            // deployment: a different person is a different sign-in.
            window.location.reload();
          }}
        >
          {nameable ? t('stub.catalog.nameable.off') : t('stub.catalog.nameable.on')}
        </Button>
      </div>
    </Alert>
  );
}
